import { fromCornDensity, type SavedCornDensity } from '/@/views/recommendations/management-recommendation-payload';

export type SavedCropConfig = {
    crop?: string;
    products?: number[];
    /** Resolved product data. Recommendations from the legacy editor carry only this. */
    productRecommendations?: { id: number }[];
    plantingDate?: string;
    environment?: number;
    climatePattern?: number;
    soilMoisture?: number;
    /** The density simulator's habitual density (plants/ha) and environment shifts (%). */
    userDensity?: number | null;
    userBetterEnvironment?: number;
    userWorseEnvironment?: number;
    [key: string]: unknown;
};

export type StoredRecommendation = {
    id?: number;
    crops?: SavedCropConfig[];
    /** Present on management recommendations, which are stored as `corn_density`. */
    cornDensity?: SavedCornDensity | null;
    backup?: Record<string, unknown> & {
        crops?: SavedCropConfig[];
        /** The retired density wizard's snapshot, which kept the product under `otherData`. */
        otherData?: { product?: { id: number } & Record<string, unknown> };
    };
    [key: string]: unknown;
};

/**
 * The crop config a stored recommendation implies.
 *
 * `backup` is the snapshot of what the user actually saw, and the result page, the PDF and the
 * share flow all read it back, so it wins over the top-level crops.
 *
 * Where the selected hybrids live depends on which flow saved the recommendation: the wizard
 * writes `products`, the top-level record writes `{crop}Products`, and anything saved by the
 * legacy editor carries neither — only the resolved `productRecommendations`, whose ids are the
 * same list in the same order. A management recommendation has none of them: it is stored as
 * `corn_density`, whose columns carry everything the sections need. Falling through all four is
 * what lets one page open recommendations from every flow that has ever saved one.
 */
export const resolveCropConfig = (
    recommendation?: StoredRecommendation,
    crop = 'corn'
): SavedCropConfig | undefined => {
    const saved = recommendation?.backup?.crops?.[0] || recommendation?.crops?.[0];
    if (saved) {
        const products = (saved.products ||
            saved[`${crop}Products`] ||
            saved.productRecommendations?.map((product) => product.id) ||
            []) as number[];
        return { ...saved, crop: saved.crop || crop, products };
    }

    if (recommendation?.cornDensity) {
        const product = recommendation.backup?.otherData?.product;
        return {
            ...fromCornDensity(recommendation.cornDensity, crop),
            ...(product ? { productRecommendations: [product] } : {}),
        };
    }

    return undefined;
};

/**
 * The blob the PDF and share endpoints render.
 *
 * The whole object is posted as the PDF payload, and the v2 template reads its hybrids off
 * `crops[0]` — which is empty on a management recommendation. Every caller that hands a
 * recommendation to those endpoints goes through here, so the document comes out the same
 * whether it was shared from the result page or from a list.
 */
export const toRenderableRecommendation = <T extends object>(recommendation: T, crop = 'corn'): T => {
    // Callers hold this as `Recommendation`, whose `crops` is the resolved API shape rather than
    // the looser stored one; the two describe the same object at different points in its life.
    const stored = recommendation as unknown as StoredRecommendation;
    const cropConfig = resolveCropConfig(stored, crop);
    return {
        ...recommendation,
        ...stored.backup,
        ...(cropConfig ? { crops: [cropConfig] } : {}),
    } as T;
};
